/**
 * Deterministic serial reference rows shaped EXACTLY like the real Cube
 * response (columns `Dbo520serial.*`): each row is one printer pair —
 * `1000/1001` = front/back face of the same machine — grouped by machine
 * line (HD / HDX / NX, …) in the `MPKey` (`520<line>-<code>_<first>-DED`).
 * Swapping to the backend needs no change here: the mock feeds the same
 * `mapSerialRows` pipeline as the real query.
 */

/** One raw Cube row (cell values as transported). */
export type MockSerialRow = Record<string, string | number | boolean>;

interface MockGroup {
  /** Machine line, e.g. `HD` (serial prefix + MPKey model suffix). */
  line: string;
  /** Printer model, e.g. `520HD` (MPKey prefix). */
  model: string;
  /** Unit codes cycling through the group's mpKeys, e.g. `H25`. */
  codes: string[];
  /** First serial number of the group; each pair consumes two numbers. */
  startNumber: number;
  /** Pairs (front/back rows) in this group. */
  pairs: number;
}

const MOCK_GROUPS: MockGroup[] = [
  { line: "HD", model: "520HD", codes: ["H25", "H27"], startNumber: 1000, pairs: 4 },
  { line: "HDX", model: "520HDX", codes: ["H10"], startNumber: 1010, pairs: 3 },
  { line: "NX", model: "520NX", codes: ["N31"], startNumber: 1020, pairs: 2 },
];

const MOCK_CUSTOMER_ID = "11480";
const MOCK_DED_SUFFIX = "DED";

/** Generates the deterministic mock rows (9 pairs across 3 machine lines). */
export function generateSerialRows(): MockSerialRow[] {
  const rows: MockSerialRow[] = [];
  for (const group of MOCK_GROUPS) {
    for (let i = 0; i < group.pairs; i++) {
      const front = group.startNumber + i * 2;
      const back = front + 1; // Front/back pair of the same printer.
      const code = group.codes[i % group.codes.length];
      rows.push({
        "Dbo520serial.MPKey": `${group.model}-${code}_${front}-${MOCK_DED_SUFFIX}`,
        "Dbo520serial.SERIAL": `${group.line}#${front}/${back}`,
        "Dbo520serial.SERIAL_1": `${front}/${back}`,
        "Dbo520serial.ID": `${MOCK_CUSTOMER_ID}-${front}-${back}`,
        "Dbo520serial.IS_SES_LINKED_DED": false,
      });
    }
  }
  return rows;
}
